import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { object, string } from "yup";
import { updateProfile } from "../../services/AuthServices";
import {
  dateSendToBackend,
  formatDateToMMDDYYYY,
  formateMobileNumber,
  getDatefromBackend,
  validateNum,
} from "../../lib/constant";
import _ from "lodash";
import "./AccountDetail.css";

const AccountDetailEdit = ({ data }) => {
  const [isDisable, setisDisable] = useState(false);

  const AdminSchema = object().shape({
    email: string()
      .required("Please enter email.")
      .email("Invalid Email Format. Please try again.")
      .trim("Please enter email."),
    firstName: string()
      .required("Please enter first name.")
      .trim("Please enter first name."),
    lastName: string()
      .required("Please enter last name.")
      .trim("Please enter last name."),
    phone: string()
      .required("Please enter Mobile number.")
      .trim("Please enter Mobile number.")
      .test("phone", "Mobile number must be exactly 10 digits", (value) => {
        return validateNum(value);
      }),
  });

  useEffect(() => {
    if (!_.isEmpty(data)) {
      setDataToProfile(data);
    }
  }, [data]);

  const submitClicked = async (formData) => {
    setisDisable(true);
    await updateProfile(formData)
      .then((response) => {
        setisDisable(false);
        toast("Profile updated successfully!", { type: "success" });
      })
      .catch((error) => {
        setisDisable(false);
        if (error?.response?.status === 422)
          toast(error?.response?.data?.message, { type: "error" });
        else if (error?.response?.status === 500)
          toast(error.response.data.message, { type: "error" });
        else
          toast(error?.response?.data?.message || "Something went wrong", {
            type: "error",
          });
      });
  };

  const {
    handleSubmit,
    handleBlur,
    setFieldValue,
    handleChange,
    values,
    errors,
    touched,
  } = useFormik({
    validationSchema: AdminSchema,
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      nationality: "",
      gender: "",
      phone: "",
      dateOfBirth: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
    },
    onSubmit: async () => {
      let formdata = new FormData();
      formdata.append("firstName", values.firstName);
      formdata.append("lastName", values.lastName);
      formdata.append("email", values.email);
      formdata.append("gender", values.gender);
      formdata.append("nationality", values.nationality);
      formdata.append("phone_number", values.phone);
      formdata.append("dateOfBirth", dateSendToBackend(values.dateOfBirth));
      formdata.append("address", values.address);
      formdata.append("city", values.city);
      formdata.append("state", values.state);
      formdata.append("pincode", values.pincode);
      submitClicked(formdata);
    },
  });

  const setDataToProfile = (profileData) => {
    setFieldValue("email", profileData?.email || "");
    setFieldValue("firstName", profileData?.first_name || "");
    setFieldValue("lastName", profileData?.last_name || "");
    setFieldValue("gender", profileData?.gender || "");
    setFieldValue("phone", formateMobileNumber(profileData?.phone_number) || "");
    setFieldValue("dateOfBirth", getDatefromBackend(profileData?.dob) || "");
    setFieldValue("address", profileData?.address || "");
    setFieldValue("city", profileData?.city || "");
    setFieldValue("state", profileData?.state || "");
    setFieldValue("pincode", profileData?.zip_code || "");
  };

  return (
    <div className="dp-profile-edit-container">
      <form onSubmit={handleSubmit}>
        {/* Personal Information Section */}
        <div className="dp-profile-card">
          <div className="dp-profile-card-header">
            <div className="dp-profile-card-title">
              <i className="far fa-user"></i>
              <h3>Personal Information</h3>
            </div>
          </div>
          <div className="dp-profile-card-body">
            <div className="dp-form-grid">
              {/* First Name */}
              <div className="dp-form-group">
                <label className="dp-form-label">First Name <span>*</span></label>
                <input
                  type="text"
                  className={`dp-form-input ${touched?.firstName && errors?.firstName ? 'error' : ''}`}
                  placeholder="Enter first name"
                  name="firstName"
                  value={values.firstName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                {touched?.firstName && errors?.firstName && (
                  <span className="dp-form-error">{errors.firstName}</span>
                )}
              </div>

              {/* Last Name */}
              <div className="dp-form-group">
                <label className="dp-form-label">Last Name <span>*</span></label>
                <input
                  type="text"
                  className={`dp-form-input ${touched?.lastName && errors?.lastName ? 'error' : ''}`}
                  placeholder="Enter last name"
                  name="lastName"
                  value={values.lastName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                {touched?.lastName && errors?.lastName && (
                  <span className="dp-form-error">{errors.lastName}</span>
                )}
              </div>

              {/* Email Address */}
              <div className="dp-form-group">
                <label className="dp-form-label">Email Address <span>*</span></label>
                <input
                  type="email"
                  className={`dp-form-input ${touched?.email && errors?.email ? 'error' : ''}`}
                  placeholder="Enter email address"
                  name="email"
                  value={values.email}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                {touched?.email && errors?.email && (
                  <span className="dp-form-error">{errors.email}</span>
                )}
              </div>

              {/* Mobile Number */}
              <div className="dp-form-group">
                <label className="dp-form-label">Mobile Number <span>*</span></label>
                <input
                  type="text"
                  className={`dp-form-input ${touched?.phone && errors?.phone ? 'error' : ''}`}
                  placeholder="Enter mobile number"
                  name="phone"
                  value={values.phone}
                  onBlur={handleBlur}
                  onChange={(e) => {
                    setFieldValue("phone", formateMobileNumber(e?.target?.value));
                  }}
                />
                {touched?.phone && errors?.phone && (
                  <span className="dp-form-error">{errors.phone}</span>
                )}
              </div>

              {/* Date of Birth */}
              <div className="dp-form-group">
                <label className="dp-form-label">Date of Birth</label>
                <div className="dp-datepicker-wrapper">
                  <ReactDatePicker
                    className="dp-form-input"
                    placeholderText="Select date of birth"
                    value={values?.dateOfBirth}
                    autoComplete="off"
                    maxDate={new Date()}
                    onChange={(e) => {
                      setFieldValue("dateOfBirth", formatDateToMMDDYYYY(new Date(e)));
                    }}
                    name="dateOfBirth"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    showMonthDropdown
                  />
                </div>
              </div>

              {/* Gender */}
              <div className="dp-form-group">
                <label className="dp-form-label">Gender</label>
                <select
                  className="dp-form-select"
                  name="gender"
                  value={values.gender}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Address Information Section */}
        <div className="dp-profile-card">
          <div className="dp-profile-card-header">
            <div className="dp-profile-card-title">
              <i className="far fa-map-marker-alt"></i>
              <h3>Address Information</h3>
            </div>
          </div>
          <div className="dp-profile-card-body">
            <div className="dp-form-grid">
              {/* Street Address */}
              <div className="dp-form-group full-width">
                <label className="dp-form-label">Street Address</label>
                <input
                  type="text"
                  className="dp-form-input"
                  placeholder="Enter your street address"
                  name="address"
                  value={values.address}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </div>

              {/* City */}
              <div className="dp-form-group">
                <label className="dp-form-label">City</label>
                <input
                  type="text"
                  className="dp-form-input"
                  placeholder="Enter city"
                  name="city"
                  value={values.city}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </div>

              {/* State */}
              <div className="dp-form-group">
                <label className="dp-form-label">State</label>
                <input
                  type="text"
                  className="dp-form-input"
                  placeholder="Enter state"
                  name="state"
                  value={values.state}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </div>

              {/* PIN Code */}
              <div className="dp-form-group">
                <label className="dp-form-label">PIN Code</label>
                <input
                  type="text"
                  className="dp-form-input"
                  placeholder="Enter PIN code"
                  name="pincode"
                  value={values.pincode}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Aadhaar Card Section */}
        <div className="dp-profile-card">
          <div className="dp-profile-card-header">
            <div className="dp-profile-card-title">
              <i className="fas fa-id-card"></i>
              <h3>Aadhaar Verification</h3>
            </div>
          </div>
          <div className="dp-profile-card-body">
            <div className="dp-aadhaar-card">
              <div className="dp-aadhaar-header">
                <div className="dp-aadhaar-title">
                  <div className="dp-aadhaar-icon">
                    <i className="fas fa-id-card"></i>
                  </div>
                  <div>
                    <h4>Aadhaar Card Verification</h4>
                    <p>Link your Aadhaar for secure identity verification</p>
                  </div>
                </div>
                <div className="dp-aadhaar-badge">
                  <i className="fas fa-clock"></i>
                  Coming Soon
                </div>
              </div>

              <div className="dp-aadhaar-coming-soon">
                <i className="fas fa-tools"></i>
                <h5>Feature Coming Soon</h5>
                <p>
                  Aadhaar verification feature is under development and will be available soon.
                  Stay tuned for updates!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="dp-form-actions" style={{ background: 'transparent', border: 'none', padding: '20px 0', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={isDisable}
            className="dp-submit-btn"
          >
            {isDisable ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountDetailEdit;
